import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Tournamentsetup.css';
import dummyImage from '../img/logo.png';
import axios from 'axios';

function TournamentSetup() {
  const navigate = useNavigate();
  const location = useLocation();

  const { tournamentData, isEdit } = location.state || {};

  const initialFormState = {
    tournamentName: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    venue: '',
    status: '',
    tournamentLogo: null,
    tournamentLogoFile: null,
    eventType: '', // Event Type ID
    eventTypeCode: '', // Event Type Code (IND/TEAM)
    eventCategories: [], // Array of selected category IDs
    breakfastStartTime: '',
    breakfastEndTime: '',
    lunchStartTime: '',
    lunchEndTime: '',
    hiTeaStartTime: '',
    hiTeaEndTime: '',
    dinnerStartTime: '',
    dinnerEndTime: '',
    sponsors: [], // Array of selected sponsor objects with category
    teams: [],
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [tournamentId, setTournamentId] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  
  // State for sponsor selection
  const [selectedSponsor, setSelectedSponsor] = useState({
    sponsorId: '',
    sponsorCategoryId: '',
    agreementDetails: ''
  });
  
  const [team, setTeam] = useState({
    teamName: '',
    teamLogo: null,
  });
  
  // State for event types (fetched from API)
  const [eventTypes, setEventTypes] = useState([]);
  
  // State for event categories (filtered by event type)
  const [allEventCategories, setAllEventCategories] = useState([]);
  const [filteredEventCategories, setFilteredEventCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // State for sponsor categories (fetched from API)
  const [sponsorCategories, setSponsorCategories] = useState([]);
  
  // State for all sponsors (fetched from API)
  const [allSponsors, setAllSponsors] = useState([]);
  
  // State for members (fetched from API)
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [filteredMembers, setFilteredMembers] = useState([]);
  
  // State for team member management
  const [selectedTeamIndex, setSelectedTeamIndex] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  
  // State for displaying tournament events info in edit mode
  const [tournamentEventsInfo, setTournamentEventsInfo] = useState([]);

  // Close member dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMemberDropdown && !event.target.closest('#searchMember')) {
        setShowMemberDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMemberDropdown]);

  // Fetch event categories on component mount
  useEffect(() => {
    fetchEventTypes();
    fetchAllEventCategories();
    fetchAllMembers();
    fetchSponsorCategories();
    fetchAllSponsors();
  }, []);

  // Fetch event types from API
  const fetchEventTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        'https://api.asterickx.com/api/EventTypes',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setEventTypes(response.data || []);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      setEventTypes([]);
    }
  };

  // Fetch all members from API
  const fetchAllMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await axios.get(
        'https://api.asterickx.com/api/Members',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setAllMembers(response.data || []);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      setAllMembers([]);
    }
  };

  // Fetch sponsor categories from API
  const fetchSponsorCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        'https://api.asterickx.com/api/SponsorCategories',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setSponsorCategories(response.data || []);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      setSponsorCategories([]);
    }
  };

  // Fetch all sponsors from API
  const fetchAllSponsors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        'https://api.asterickx.com/api/Sponsors',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setAllSponsors(response.data || []);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      setAllSponsors([]);
    }
  };

  // Filter categories when event type changes
  useEffect(() => {
    if (formData.eventType && allEventCategories.length > 0) {
      filterCategoriesByEventType(formData.eventType);
    }
  }, [formData.eventType, allEventCategories]);

  useEffect(() => {
    const loadEditData = async () => {
      if (!isEdit || !tournamentData) return;

      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      const formatTimeForInput = (timeString) => {
        if (!timeString) return '';
        return timeString.split(':').slice(0, 2).join(':');
      };

      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch full tournament data including sponsors using the /full endpoint
        const fullTournamentResponse = await axios.get(
          `https://api.asterickx.com/api/Tournaments/${tournamentData.id}/full`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const fullTournamentData = fullTournamentResponse.data;

        // Extract meal timings from sessions array
        const sessions = fullTournamentData.sessions || [];
        const getSessionTime = (name, isStart) => {
          const session = sessions.find(s => s.name === name);
          if (!session) return '';
          const dateTime = isStart ? session.startDateTime : session.endDateTime;
          if (!dateTime) return '';
          return dateTime.split('T')[1]?.split(':').slice(0, 2).join(':') || '';
        };

        // Map teams - API uses teamId and teamName
        const mappedTeams = (fullTournamentData.teams || []).map(team => ({
          id: team.teamId,
          teamName: team.teamName,
          teamLogo: team.logoPath 
            ? `https://api.asterickx.com${team.logoPath}` 
            : null,
          teamLogoFile: null,
          members: (team.members || []).map(member => ({
            memberId: member.memberId,
            firstName: member.firstName,
            lastName: member.lastName,
            email: member.email,
            phoneNumber: member.mobileNumber || '',
            isExisting: true
          }))
        }));

        // Map sponsors - Now using data from /full endpoint
        const mappedSponsors = (fullTournamentData.sponsors || []).map(sponsor => ({
          id: sponsor.id, // Store the TournamentSponsor ID to prevent re-adding
          sponsorId: sponsor.id,
          sponsorName: sponsor.name,
          sponsorLogo: sponsor.logoPath 
            ? `https://api.asterickx.com${sponsor.logoPath}` 
            : null,
          sponsorCategoryId: sponsor.categoryId || '',
          agreementDetails: sponsor.agreementDetails || ''
        }));

        // Get event type
        let eventTypeId = '';
        let eventTypeCode = '';
        let categoryIds = [];

        // Fetch from TournamentEvents API in edit mode
        try {
          const tournamentEventsResponse = await axios.get(
            `https://api.asterickx.com/api/TournamentEvents?tournamentId=${tournamentData.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (tournamentEventsResponse.data) {
            const data = tournamentEventsResponse.data;
            
            if (Array.isArray(data) && data.length > 0) {
              const firstEvent = data[0];
              
              if (firstEvent.eventType) {
                eventTypeId = firstEvent.eventType.id || '';
                eventTypeCode = firstEvent.eventType.code || '';
              } else if (firstEvent.eventTypeId) {
                eventTypeId = firstEvent.eventTypeId;
                const foundType = eventTypes.find(t => t.id === eventTypeId);
                eventTypeCode = foundType?.code || '';
              } else if (firstEvent.eventCategory?.eventTypeId) {
                eventTypeId = firstEvent.eventCategory.eventTypeId;
                const foundType = eventTypes.find(t => t.id === eventTypeId);
                eventTypeCode = foundType?.code || '';
              }
              
              categoryIds = data
                .map(item => {
                  if (item.eventCategory) {
                    return item.eventCategory.id;
                  }
                  if (item.eventCategoryId) {
                    return item.eventCategoryId;
                  }
                  return null;
                })
                .filter(id => id !== null);
              
              categoryIds = [...new Set(categoryIds)];
              
              // Store tournament events info for display
              const eventsDisplayInfo = data
                .filter(item => item.tournamentId === tournamentData.id)
                .map(item => {
                  const extractedEventTypeId = 
                    item.eventTypeId || 
                    item.eventType?.id || 
                    item.eventCategory?.eventTypeId || 
                    '';
                  
                  const extractedCategoryId = 
                    item.eventCategoryId || 
                    item.eventCategory?.id || 
                    '';
                  
                  return {
                    id: item.id,
                    tournamentId: item.tournamentId,
                    tournamentName: item.tournament?.name || fullTournamentData.name || '',
                    eventTypeId: extractedEventTypeId,
                    eventTypeName: item.eventType?.displayName || item.eventType?.name || '',
                    eventTypeCode: item.eventType?.code || '',
                    categoryId: extractedCategoryId,
                    categoryName: item.eventCategory?.displayName || item.eventCategory?.name || '',
                    categoryCode: item.eventCategory?.code || '',
                    ageGroup: item.eventCategory?.ageGroup || '',
                    gender: item.eventCategory?.gender || '',
                    isDoubles: item.eventCategory?.isDoubles || false
                  };
                });
              
              setTournamentEventsInfo(eventsDisplayInfo);
            }
          }
        } catch (error) {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            navigate('/login');
            return;
          }
        }

        setFormData({
          tournamentName: fullTournamentData.name || '',
          startDate: formatDateForInput(fullTournamentData.startDate),
          endDate: formatDateForInput(fullTournamentData.endDate),
          startTime: formatTimeForInput(fullTournamentData.startTime),
          endTime: formatTimeForInput(fullTournamentData.endTime),
          venue: fullTournamentData.venue || '',
          status: fullTournamentData.status ? fullTournamentData.status.toLowerCase() : 'active',
          tournamentLogo: fullTournamentData.logoPath 
            ? `https://api.asterickx.com${fullTournamentData.logoPath}` 
            : null,
          tournamentLogoFile: null,
          eventType: eventTypeId,
          eventTypeCode: eventTypeCode,
          eventCategories: categoryIds,
          breakfastStartTime: getSessionTime('Breakfast', true),
          breakfastEndTime: getSessionTime('Breakfast', false),
          lunchStartTime: getSessionTime('Lunch', true),
          lunchEndTime: getSessionTime('Lunch', false),
          hiTeaStartTime: getSessionTime('Hi-Tea', true),
          hiTeaEndTime: getSessionTime('Hi-Tea', false),
          dinnerStartTime: getSessionTime('Dinner', true),
          dinnerEndTime: getSessionTime('Dinner', false),
          sponsors: mappedSponsors,
          teams: mappedTeams,
        });

        setTournamentId(tournamentData.id);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      }
    };

    loadEditData();
  }, [isEdit, tournamentData, eventTypes, navigate]);

  const fetchAllEventCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        'https://api.asterickx.com/api/EventCategories',
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setAllEventCategories(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const filterCategoriesByEventType = (eventTypeId) => {
    if (!allEventCategories || allEventCategories.length === 0) {
      setFilteredEventCategories([]);
      return;
    }

    const filtered = allEventCategories.filter(
      category => category.eventTypeId === eventTypeId
    );

    setFilteredEventCategories(filtered);

    // Clear selected categories that don't match new event type
    const validCategoryIds = filtered.map(cat => cat.id);
    setFormData(prev => ({
      ...prev,
      eventCategories: prev.eventCategories.filter(id => validCategoryIds.includes(id))
    }));
  };

  const formatTimeWithSeconds = (time) => {
    if (time.includes(':') && time.split(':').length === 2) {
      return `${time}:00`;
    }
    return time;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleEventTypeChange = (e) => {
    const eventTypeId = e.target.value;
    
    const selectedEventType = eventTypes.find(type => type.id === eventTypeId);
    const eventTypeCode = selectedEventType ? selectedEventType.code : '';
    
    setFormData({
      ...formData,
      eventType: eventTypeId,
      eventTypeCode: eventTypeCode,
      eventCategories: [],
      teams: eventTypeCode === 'IND' ? [] : formData.teams
    });
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      eventCategories: prev.eventCategories.includes(categoryId)
        ? prev.eventCategories.filter(id => id !== categoryId)
        : [...prev.eventCategories, categoryId]
    }));
  };

  const handleLogoChange = (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      const newLogo = URL.createObjectURL(files[0]);
      setFormData({
        ...formData,
        tournamentLogo: newLogo,
        tournamentLogoFile: files[0],
      });
    }
  };

  // Handle sponsor selection change
  const handleSponsorSelectionChange = (e) => {
    const { name, value } = e.target;
    setSelectedSponsor({
      ...selectedSponsor,
      [name]: value,
    });
  };

  const handleTeamChange = (e) => {
    const { name, value } = e.target;
    setTeam({
      ...team,
      [name]: value,
    });
  };

  const handleTeamFileChange = (e) => {
    const { name, files } = e.target;
    setTeam({
      ...team,
      [name]: files[0],
    });
  };

  // Add selected sponsor to tournament
  const addSponsorToTournament = () => {
    if (!selectedSponsor.sponsorId || !selectedSponsor.sponsorCategoryId) {
      alert('Please select both sponsor and sponsor category');
      return;
    }

    // Check if sponsor already added
    const alreadyAdded = formData.sponsors.some(s => s.sponsorId === selectedSponsor.sponsorId);
    if (alreadyAdded) {
      alert('This sponsor has already been added to the tournament');
      return;
    }

    // Get sponsor details from allSponsors
    const sponsorDetails = allSponsors.find(s => s.id === selectedSponsor.sponsorId);
    
    if (!sponsorDetails) {
      alert('Sponsor not found');
      return;
    }

    const newSponsor = {
      sponsorId: selectedSponsor.sponsorId,
      sponsorName: sponsorDetails.name,
      sponsorLogo: sponsorDetails.logoPath 
        ? `https://api.asterickx.com${sponsorDetails.logoPath}` 
        : null,
      sponsorCategoryId: selectedSponsor.sponsorCategoryId,
      agreementDetails: selectedSponsor.agreementDetails
    };

    setFormData(prevData => ({
      ...prevData,
      sponsors: [...prevData.sponsors, newSponsor],
    }));

    // Reset selection
    setSelectedSponsor({ 
      sponsorId: '',
      sponsorCategoryId: '',
      agreementDetails: ''
    });
  };

  const addTeam = () => {
    if (team.teamName.trim()) {
      const newTeam = {
        teamName: team.teamName.trim(),
        teamLogo: team.teamLogo,
        members: []
      };

      setFormData(prevData => ({
        ...prevData,
        teams: [...prevData.teams, newTeam],
      }));

      setTeam({ teamName: '', teamLogo: null });

      const teamLogoInput = document.getElementById('teamLogo');
      if (teamLogoInput) teamLogoInput.value = '';
    } else {
      alert('Please enter team name');
    }
  };

  const openMemberModal = (teamIndex) => {
    setSelectedTeamIndex(teamIndex);
    setShowMemberModal(true);
    setSelectedMemberId('');
    setMemberSearchQuery('');
    setShowMemberDropdown(false);
    filterAvailableMembers('', teamIndex);
  };

  // Filter members based on search query and exclude already selected members from ALL teams
  const filterAvailableMembers = (searchQuery, teamIndex) => {
    const allSelectedMemberIds = [];
    
    formData.teams.forEach((team, index) => {
      if (team.members && Array.isArray(team.members)) {
        team.members.forEach(member => {
          if (member.memberId) {
            allSelectedMemberIds.push(member.memberId);
          }
        });
      }
    });
    
    let available = allMembers.filter(member => 
      !allSelectedMemberIds.includes(member.id)
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      available = available.filter(member => 
        member.firstName?.toLowerCase().includes(query) ||
        member.lastName?.toLowerCase().includes(query) ||
        member.email?.toLowerCase().includes(query) ||
        member.mobileNumber?.includes(query)
      );
    }

    setFilteredMembers(available);
  };

  const handleMemberSearch = (e) => {
    const query = e.target.value;
    setMemberSearchQuery(query);
    setShowMemberDropdown(true);
    filterAvailableMembers(query, selectedTeamIndex);
  };

  const handleMemberSelect = (memberId) => {
    setSelectedMemberId(memberId);
    const member = allMembers.find(m => m.id === memberId);
    if (member) {
      setMemberSearchQuery(`${member.firstName} ${member.lastName} (${member.email})`);
    }
    setShowMemberDropdown(false);
  };

  const addMemberToTeam = async () => {
    if (!selectedMemberId) {
      alert('Please select a member');
      return;
    }

    const currentTeam = formData.teams[selectedTeamIndex];
    
    const selectedMember = allMembers.find(m => m.id === selectedMemberId);
    if (!selectedMember) {
      alert('Member not found');
      return;
    }

    // If team has an ID (already saved), make API call
    if (currentTeam.id) {
      try {
        const token = localStorage.getItem('token');
        const memberData = {
          memberId: selectedMemberId,
          teamId: currentTeam.id
        };

        await axios.post(
          `https://api.asterickx.com/api/Teams/${currentTeam.id}/members`,
          memberData,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        alert('Member added successfully!');
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        alert('Error adding member. Please try again.');
        return;
      }
    }

    // Update local state
    const updatedTeams = [...formData.teams];
    updatedTeams[selectedTeamIndex].members = [
      ...(updatedTeams[selectedTeamIndex].members || []),
      {
        memberId: selectedMemberId,
        firstName: selectedMember.firstName,
        lastName: selectedMember.lastName,
        email: selectedMember.email,
        phoneNumber: selectedMember.phoneNumber || '',
        isExisting: currentTeam.id ? true : false
      }
    ];

    setFormData(prev => ({
      ...prev,
      teams: updatedTeams
    }));

    setSelectedMemberId('');
  };

  const removeMemberFromTeam = async (teamIndex, memberIndex) => {
    const team = formData.teams[teamIndex];
    const member = team.members[memberIndex];
    
    // If existing member, call DELETE API
    if (member.memberId && team.id && isEdit) {
      try {
        const token = localStorage.getItem('token');
        const deleteUrl = `https://api.asterickx.com/api/Teams/${team.id}/members/${member.memberId}`;
        
        await axios.delete(deleteUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        alert('Failed to remove member from team. Please try again.');
        return;
      }
    }
    
    // Update local state
    const updatedTeams = [...formData.teams];
    updatedTeams[teamIndex].members = updatedTeams[teamIndex].members.filter(
      (_, i) => i !== memberIndex
    );
    setFormData(prev => ({
      ...prev,
      teams: updatedTeams
    }));
  };

  const saveMealTimings = async (tournamentId) => {
    try {
      const token = localStorage.getItem('token');
      const sessions = [];

      if (formData.breakfastStartTime && formData.breakfastEndTime) {
        sessions.push({
          tournamentId: tournamentId,
          name: 'Breakfast',
          startDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.breakfastStartTime)}`,
          endDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.breakfastEndTime)}`,
          description: 'Breakfast session'
        });
      }

      if (formData.lunchStartTime && formData.lunchEndTime) {
        sessions.push({
          tournamentId: tournamentId,
          name: 'Lunch',
          startDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.lunchStartTime)}`,
          endDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.lunchEndTime)}`,
          description: 'Lunch session'
        });
      }

      if (formData.hiTeaStartTime && formData.hiTeaEndTime) {
        sessions.push({
          tournamentId: tournamentId,
          name: 'Hi-Tea',
          startDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.hiTeaStartTime)}`,
          endDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.hiTeaEndTime)}`,
          description: 'Hi-Tea session'
        });
      }

      if (formData.dinnerStartTime && formData.dinnerEndTime) {
        sessions.push({
          tournamentId: tournamentId,
          name: 'Dinner',
          startDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.dinnerStartTime)}`,
          endDateTime: `${formData.startDate}T${formatTimeWithSeconds(formData.dinnerEndTime)}`,
          description: 'Dinner session'
        });
      }

      for (const session of sessions) {
        await axios.post(
          'https://api.asterickx.com/api/TournamentSessions',
          session,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      return true;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      return false;
    }
  };

  const saveTournamentDetails = async () => {
    setLoading(true);
    setSuccessMessage('');

    try {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        alert('End date must be after the start date.');
        setLoading(false);
        return false;
      }

      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/login');
        setLoading(false);
        return false;
      }

      const tournamentPayload = {
        name: formData.tournamentName.trim(),
        startDate: formData.startDate,
        endDate: formData.endDate,
        startTime: formatTimeWithSeconds(formData.startTime),
        endTime: formatTimeWithSeconds(formData.endTime),
        venue: formData.venue.trim(),
        status: formData.status,
        eventTypeId: formData.eventType,
        eventCategoryIds: formData.eventCategories,
      };

      const tournamentFormData = new FormData();

      Object.keys(tournamentPayload).forEach(key => {
        if (key === 'eventCategoryIds') {
          tournamentFormData.append('EventCategoryIds', JSON.stringify(tournamentPayload[key]));
        } else {
          tournamentFormData.append(key.charAt(0).toUpperCase() + key.slice(1), tournamentPayload[key]);
        }
      });

      if (formData.tournamentLogoFile) {
        tournamentFormData.append('Logo', formData.tournamentLogoFile);
      }

      let response;
      if (isEdit && tournamentId) {
        tournamentFormData.append('Id', tournamentId);
        setSuccessMessage('Updating tournament details...');
        response = await axios.put(
          `https://api.asterickx.com/api/Tournaments/${tournamentId}`,
          tournamentFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        await saveMealTimings(tournamentId);
        await saveTournamentEvents(tournamentId);
      } else {
        setSuccessMessage('Creating tournament...');
        response = await axios.post(
          'https://api.asterickx.com/api/Tournaments',
          tournamentFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const newTournamentId = response.data.id || 
          response.data.Id ||
          response.data.tournamentId ||
          response.data.TournamentId;

        if (!newTournamentId) {
          throw new Error('Tournament ID not received from server');
        }

        setTournamentId(newTournamentId);

        await saveMealTimings(newTournamentId);
        await saveTournamentEvents(newTournamentId);
      }

      setSuccessMessage('Tournament details saved successfully!');
      setLoading(false);
      return true;

    } catch (error) {
      setSuccessMessage('');

      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else if (error.response && error.response.status === 400) {
        alert(`Bad Request: ${error.response.data.message || 'Please check your input data'}`);
      } else if (error.message) {
        alert(`Error: ${error.message}`);
      } else {
        alert('An error occurred. Please try again.');
      }

      setLoading(false);
      return false;
    }
  };

  const saveTournamentEvents = async (tournamentId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Create a tournament event entry for each selected category
      for (const categoryId of formData.eventCategories) {
        const tournamentEventData = {
          tournamentId: tournamentId,
          eventTypeId: formData.eventType,
          eventCategoryId: categoryId
        };

        await axios.post(
          'https://api.asterickx.com/api/TournamentEvents',
          tournamentEventData,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      return true;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
      return false;
    }
  };

  const saveTeams = async () => {
    if (formData.eventTypeCode === 'IND') {
      setCurrentStep(3);
      return true;
    }

    if (formData.teams.length === 0) {
      setCurrentStep(3);
      return true;
    }

    setLoading(true);
    setSuccessMessage('Saving teams...');

    try {
      const token = localStorage.getItem('token');

      for (let i = 0; i < formData.teams.length; i++) {
        const team = formData.teams[i];
        let teamId = team.id;

        if (!team.id) {
          const teamFormData = new FormData();
          teamFormData.append('TournamentId', tournamentId);
          teamFormData.append('Name', (team.teamName || '').trim());
          
          if (team.teamLogoFile) {
            teamFormData.append('Logo', team.teamLogoFile);
          }

          const teamResponse = await axios.post(
            'https://api.asterickx.com/api/Teams',
            teamFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
              },
            }
          );

          teamId = teamResponse.data.id || teamResponse.data.Id;

          const updatedTeams = [...formData.teams];
          updatedTeams[i].id = teamId;
          setFormData(prev => ({ ...prev, teams: updatedTeams }));
        }

        if (team.members && team.members.length > 0) {
          for (const member of team.members) {
            if (!member.isExisting) {
              try {
                await axios.post(
                  `https://api.asterickx.com/api/Teams/${teamId}/members`,
                  {
                    memberId: member.memberId || member.id,
                    teamId: teamId
                  },
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
              } catch (memberError) {
                if (memberError.response && memberError.response.status === 401) {
                  localStorage.removeItem('token');
                  navigate('/login');
                  return false;
                }
              }
            }
          }
        }

        if (i < formData.teams.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setSuccessMessage('Teams saved successfully!');
      setLoading(false);
      return true;

    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        alert('Error saving teams. Please try again.');
      }
      setLoading(false);
      return false;
    }
  };

  const saveSponsors = async () => {
    if (formData.sponsors.length === 0) {
      setShowSuccessAlert(true);
      setTimeout(() => {
        setShowSuccessAlert(false);
        setFormData(initialFormState);
        setCurrentStep(1);
        setTournamentId(null);
        navigate('/tournament-list');
      }, 2000);
      return true;
    }

    setLoading(true);
    setSuccessMessage('Assigning sponsors to tournament...');

    try {
      const token = localStorage.getItem('token');

      for (let i = 0; i < formData.sponsors.length; i++) {
        const sponsor = formData.sponsors[i];

        // Skip if already assigned in edit mode (has id property)
        if (sponsor.id && isEdit) continue;

        // Link sponsor to tournament using TournamentSponsors API
        const tournamentSponsorData = {
          tournamentId: tournamentId,
          sponsorId: sponsor.sponsorId,
          sponsorCategoryId: sponsor.sponsorCategoryId,
          agreementDetails: sponsor.agreementDetails || ''
        };

        await axios.post(
          'https://api.asterickx.com/api/TournamentSponsors',
          tournamentSponsorData,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (i < formData.sponsors.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      setSuccessMessage('');
      setShowSuccessAlert(true);
      setLoading(false);

      setTimeout(() => {
        setShowSuccessAlert(false);
        setFormData(initialFormState);
        setCurrentStep(1);
        setTournamentId(null);
        setSelectedSponsor({ sponsorId: '', sponsorCategoryId: '', agreementDetails: '' });
        setTeam({ teamName: '', teamLogo: null });
        navigate('/tournament');
      }, 2000);

      return true;

    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        alert('Error assigning sponsors. Please try again.');
      }
      setLoading(false);
      return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const success = await saveTournamentDetails();
      if (success) {
        if (formData.eventTypeCode === 'IND') {
          setCurrentStep(3);
        } else {
          setCurrentStep(2);
        }
        setSuccessMessage('');
      }
    } else if (currentStep === 2) {
      const success = await saveTeams();
      if (success) {
        setCurrentStep(3);
        setSuccessMessage('');
      }
    } else if (currentStep === 3) {
      await saveSponsors();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      if (currentStep === 3 && formData.eventTypeCode === 'IND') {
        setCurrentStep(1);
      } else {
        setCurrentStep(currentStep - 1);
      }
      setSuccessMessage('');
    }
  };

  const validateStep1 = () => {
    return formData.tournamentName.trim() &&
      formData.startDate &&
      formData.endDate &&
      formData.startTime &&
      formData.endTime &&
      formData.venue.trim() &&
      formData.status &&
      formData.eventType &&
      formData.eventCategories.length > 0;
  };

  const getSelectedCategoryNames = () => {
    return filteredEventCategories
      .filter(cat => formData.eventCategories.includes(cat.id))
      .map(cat => {
        let categoryName = cat.displayName || cat.code;
        if (cat.gender) categoryName += ` - ${cat.gender}`;
        if (cat.ageGroup) categoryName += ` (${cat.ageGroup})`;
        if (cat.isDoubles) categoryName += ' [Doubles]';
        return categoryName;
      })
      .join(', ');
  };

  return (
    <div className="ts-dashboard-wrapper">
      <div className="ts-main-content">
        <main className="ts-content-area">
          <div className="ts-fullscreen-wrapper">
            <div className="ts-form-container container-fluid">

              {showSuccessAlert && (
                <div className="alert alert-success alert-dismissible fade show" role="alert" style={{
                  position: 'fixed',
                  top: '20px',
                  right: '20px',
                  zIndex: 9999,
                  minWidth: '300px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  <strong>Success!</strong> {isEdit ? 'Tournament updated successfully!' : 'Tournament created successfully!'}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSuccessAlert(false)}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {successMessage && <p className="ts-success-msg text-center">{successMessage}</p>}

              <h2 className="text-center mb-4">{isEdit ? 'Edit Tournament' : 'Create New Tournament'}</h2>

              {/* Progress Indicator */}
              <div className="ts-step-progress">
                <div className={`ts-progress-step ${currentStep >= 1 ? 'ts-step-active' : ''} ${currentStep > 1 ? 'ts-step-done' : ''}`}>
                  <div className="ts-progress-circle">1</div>
                  <div className="ts-progress-text">Tournament Details</div>
                </div>
                {formData.eventTypeCode === 'TEAM' && (
                  <>
                    <div className="ts-progress-connector"></div>
                    <div className={`ts-progress-step ${currentStep >= 2 ? 'ts-step-active' : ''} ${currentStep > 2 ? 'ts-step-done' : ''}`}>
                      <div className="ts-progress-circle">2</div>
                      <div className="ts-progress-text">Add Teams</div>
                    </div>
                  </>
                )}
                <div className="ts-progress-connector"></div>
                <div className={`ts-progress-step ${currentStep >= 3 ? 'ts-step-active' : ''}`}>
                  <div className="ts-progress-circle">{formData.eventTypeCode === 'TEAM' ? '3' : '2'}</div>
                  <div className="ts-progress-text">Assign Sponsors</div>
                </div>
              </div>

              {/* Step 1: Tournament Details */}
              {currentStep === 1 && (
                <div className="ts-step-panel">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="tournamentName">Tournament Name: *</label>
                        <input
                          id="tournamentName"
                          type="text"
                          name="tournamentName"
                          className="ts-input-field"
                          value={formData.tournamentName}
                          onChange={handleChange}
                          placeholder="Enter tournament name"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="tournamentLogo">Tournament Logo:</label>
                        <div className="ts-logo-upload-wrapper">
                          <div className="ts-logo-preview-box" onClick={() => document.getElementById('logoInput').click()}>
                            <img
                              src={formData.tournamentLogo || dummyImage}
                              alt="Tournament Logo"
                              className="ts-logo-image"
                            />
                            <div className="ts-logo-overlay">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                <circle cx="12" cy="13" r="4"></circle>
                              </svg>
                              <span>Click to upload</span>
                            </div>
                          </div>
                          <input
                            id="logoInput"
                            type="file"
                            name="tournamentLogo"
                            className="ts-file-input"
                            onChange={handleLogoChange}
                            accept="image/*"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Event Type and Categories */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="eventType">Event Type: *</label>
                        <select
                          id="eventType"
                          name="eventType"
                          className="ts-input-field"
                          value={formData.eventType}
                          onChange={handleEventTypeChange}
                          required
                        >
                          <option value="">Select Event Type</option>
                          {eventTypes.map(type => (
                            <option key={type.id} value={type.id}>
                              {type.displayName}
                            </option>
                          ))}
                        </select>
                        {formData.eventType && (
                          <div className="ts-event-type-badge">
                            <span className="ts-badge-icon">
                              {formData.eventTypeCode === 'IND' ? '👤' : '👥'}
                            </span>
                            <span className="ts-badge-text">
                              {formData.eventTypeCode === 'IND' 
                                ? 'Individual Competition' 
                                : 'Team Competition'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="eventCategories">Event Categories: *</label>
                        <div className="ts-multi-select-wrapper">
                          <div 
                            className="ts-multi-select-display ts-input-field"
                            onClick={() => formData.eventType && setShowCategoryDropdown(!showCategoryDropdown)}
                            style={{ 
                              cursor: formData.eventType ? 'pointer' : 'not-allowed',
                              opacity: formData.eventType ? 1 : 0.6
                            }}
                          >
                            {!formData.eventType 
                              ? '👆 Please select event type first...'
                              : formData.eventCategories.length > 0 
                                ? getSelectedCategoryNames() 
                                : 'Click to select categories...'}
                          </div>
                          {showCategoryDropdown && formData.eventType && (
                            <div className="ts-multi-select-dropdown">
                              {filteredEventCategories.length === 0 ? (
                                <div className="ts-no-categories">
                                  <div className="ts-no-categories-icon">📂</div>
                                  <p>No categories available for this event type</p>
                                </div>
                              ) : (
                                <>
                                  <div className="ts-dropdown-header">
                                    Select Categories ({filteredEventCategories.length} available)
                                  </div>
                                  {filteredEventCategories.map(category => {
                                    let categoryLabel = category.displayName || category.code;
                                    if (category.gender) categoryLabel += ` - ${category.gender}`;
                                    if (category.ageGroup) categoryLabel += ` (${category.ageGroup})`;
                                    if (category.isDoubles) categoryLabel += ' [Doubles]';
                                    
                                    return (
                                      <label key={category.id} className="ts-multi-select-option">
                                        <input
                                          type="checkbox"
                                          checked={formData.eventCategories.includes(category.id)}
                                          onChange={() => handleCategoryToggle(category.id)}
                                        />
                                        <span>{categoryLabel}</span>
                                      </label>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {!formData.eventType && (
                          <small className="ts-helper-text">
                            ℹ️ Select an event type above to view available categories
                          </small>
                        )}
                        {formData.eventType && filteredEventCategories.length === 0 && (
                          <small className="ts-helper-text text-warning">
                            ⚠️ No categories found for {formData.eventTypeCode === 'IND' ? 'Individual' : 'Team'} events
                          </small>
                        )}
                        {formData.eventCategories.length > 0 && (
                          <small className="ts-helper-text text-success">
                            ✓ {formData.eventCategories.length} {formData.eventCategories.length === 1 ? 'category' : 'categories'} selected
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Display Tournament Events Info in Edit Mode */}
                  {isEdit && tournamentEventsInfo.length > 0 && (
                    <div className="row">
                      <div className="col-md-12 mb-4">
                        <div style={{
                          backgroundColor: '#f0f9ff',
                          border: '2px solid #0ea5e9',
                          borderRadius: '12px',
                          padding: '20px',
                          marginTop: '10px'
                        }}>
                          <h4 style={{
                            fontSize: '16px',
                            fontWeight: '700',
                            color: '#0c4a6e',
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '20px' }}>🏆</span>
                            Tournament Events - {formData.tournamentName}
                          </h4>
                          
                          <div style={{
                            marginBottom: '16px',
                            padding: '10px',
                            backgroundColor: '#e0f2fe',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#0c4a6e',
                            fontWeight: '500'
                          }}>
                            📋 Showing events only for this tournament
                          </div>
                          
                          {/* Event Type Section - Show Once */}
                          {tournamentEventsInfo.length > 0 && (
                            <div style={{
                              backgroundColor: 'white',
                              border: '2px solid #bae6fd',
                              borderRadius: '10px',
                              padding: '20px',
                              marginBottom: '16px'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '16px'
                              }}>
                                <div>
                                  <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#64748b',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    marginBottom: '6px'
                                  }}>
                                    Event Type
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontSize: '20px' }}>
                                      {tournamentEventsInfo[0].eventTypeCode === 'IND' ? '👤' : '👥'}
                                    </span>
                                    <span style={{
                                      fontSize: '16px',
                                      fontWeight: '700',
                                      color: '#0f172a'
                                    }}>
                                      {tournamentEventsInfo[0].eventTypeName || 
                                       (tournamentEventsInfo[0].eventTypeCode === 'IND' ? 'Individual Event' : 
                                        tournamentEventsInfo[0].eventTypeCode === 'TEAM' ? 'Team Event' : 
                                        'Event Type')}
                                    </span>
                                    <span style={{
                                      fontSize: '12px',
                                      padding: '4px 10px',
                                      backgroundColor: '#e0f2fe',
                                      color: '#0369a1',
                                      borderRadius: '6px',
                                      fontWeight: '600'
                                    }}>
                                      {tournamentEventsInfo[0].eventTypeCode || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                                
                                <button
                                  type="button"
                                  onClick={() => {
                                    document.getElementById('eventType')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    document.getElementById('eventType')?.focus();
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#0ea5e9',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  <span>✏️</span>
                                  Edit
                                </button>
                              </div>
                              
                              {/* Categories List */}
                              <div>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#64748b',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '12px',
                                  paddingTop: '12px',
                                  borderTop: '1px solid #e2e8f0'
                                }}>
                                  Event Categories ({tournamentEventsInfo.length})
                                </div>
                                
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                  gap: '12px'
                                }}>
                                  {tournamentEventsInfo.map((event, index) => (
                                    <div
                                      key={event.id || index}
                                      style={{
                                        padding: '12px',
                                        backgroundColor: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px'
                                      }}
                                    >
                                      <div style={{
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        color: '#0c4a6e',
                                        marginBottom: '8px'
                                      }}>
                                        {event.categoryName || event.categoryCode || `Category ${index + 1}`}
                                      </div>
                                      
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {event.categoryCode && (
                                          <span style={{
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            backgroundColor: '#f1f5f9',
                                            color: '#475569',
                                            borderRadius: '4px',
                                            fontWeight: '600'
                                          }}>
                                            {event.categoryCode}
                                          </span>
                                        )}
                                        {event.gender && (
                                          <span style={{
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            backgroundColor: event.gender === 'Male' ? '#dbeafe' : '#fce7f3',
                                            color: event.gender === 'Male' ? '#1e40af' : '#be185d',
                                            borderRadius: '4px',
                                            fontWeight: '600'
                                          }}>
                                            {event.gender}
                                          </span>
                                        )}
                                        {event.ageGroup && (
                                          <span style={{
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            backgroundColor: '#fef3c7',
                                            color: '#92400e',
                                            borderRadius: '4px',
                                            fontWeight: '600'
                                          }}>
                                            {event.ageGroup}
                                          </span>
                                        )}
                                        {event.isDoubles && (
                                          <span style={{
                                            fontSize: '11px',
                                            padding: '3px 8px',
                                            backgroundColor: '#dcfce7',
                                            color: '#166534',
                                            borderRadius: '4px',
                                            fontWeight: '600'
                                          }}>
                                            Doubles
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            backgroundColor: '#e0f2fe',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: '#0c4a6e',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '16px' }}>ℹ️</span>
                            <span>
                              <strong>{tournamentEventsInfo.length}</strong> {tournamentEventsInfo.length === 1 ? 'event' : 'events'} for <strong>{formData.tournamentName}</strong> loaded from database
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="startDate">Start Date: *</label>
                        <input
                          id="startDate"
                          type="date"
                          name="startDate"
                          className="ts-input-field"
                          value={formData.startDate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="endDate">End Date: *</label>
                        <input
                          id="endDate"
                          type="date"
                          name="endDate"
                          className="ts-input-field"
                          value={formData.endDate}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="startTime">Start Time: *</label>
                        <input
                          id="startTime"
                          type="time"
                          name="startTime"
                          className="ts-input-field ts-time-field"
                          value={formData.startTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="endTime">End Time: *</label>
                        <input
                          id="endTime"
                          type="time"
                          name="endTime"
                          className="ts-input-field ts-time-field"
                          value={formData.endTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="venue">Venue: *</label>
                        <input
                          id="venue"
                          type="text"
                          name="venue"
                          className="ts-input-field"
                          value={formData.venue}
                          onChange={handleChange}
                          placeholder="Enter venue location"
                          maxLength={100}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <div className="ts-field-group">
                        <label htmlFor="status">Status: *</label>
                        <div className="ts-radio-wrapper">
                          <label className="ts-radio-option">
                            <input
                              type="radio"
                              name="status"
                              value="active"
                              checked={formData.status === 'active'}
                              onChange={handleChange}
                              required
                            />
                            <span className="ts-radio-label">Active</span>
                          </label>
                          <label className="ts-radio-option">
                            <input
                              type="radio"
                              name="status"
                              value="inactive"
                              checked={formData.status === 'inactive'}
                              onChange={handleChange}
                              required
                            />
                            <span className="ts-radio-label">Inactive</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ts-meal-schedule">
                    <h4 className="ts-meal-title">
                      <span className="ts-meal-icon">🍽️</span>
                      Meal Timings (Optional)
                    </h4>

                    <div className="ts-meal-grid">
                      <div className="ts-meal-card">
                        <div className="ts-meal-header">
                          <span className="ts-meal-emoji">🌅</span>
                          <h5>Breakfast</h5>
                        </div>
                        <div className="ts-meal-times">
                          <div className="ts-time-group">
                            <span className="ts-time-tag">Start Time</span>
                            <input
                              type="time"
                              name="breakfastStartTime"
                              className="ts-time-field"
                              value={formData.breakfastStartTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="ts-time-group">
                            <span className="ts-time-tag">End Time</span>
                            <input
                              type="time"
                              name="breakfastEndTime"
                              className="ts-time-field"
                              value={formData.breakfastEndTime}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="ts-meal-card">
                        <div className="ts-meal-header">
                          <span className="ts-meal-emoji">☀️</span>
                          <h5>Lunch</h5>
                        </div>
                        <div className="ts-meal-times">
                          <div className="ts-time-group">
                            <span className="ts-time-tag">Start Time</span>
                            <input
                              type="time"
                              name="lunchStartTime"
                              className="ts-time-field"
                              value={formData.lunchStartTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="ts-time-group">
                            <span className="ts-time-tag">End Time</span>
                            <input
                              type="time"
                              name="lunchEndTime"
                              className="ts-time-field"
                              value={formData.lunchEndTime}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="ts-meal-card">
                        <div className="ts-meal-header">
                          <span className="ts-meal-emoji">☕</span>
                          <h5>Hi-Tea</h5>
                        </div>
                        <div className="ts-meal-times">
                          <div className="ts-time-group">
                            <span className="ts-time-tag">Start Time</span>
                            <input
                              type="time"
                              name="hiTeaStartTime"
                              className="ts-time-field"
                              value={formData.hiTeaStartTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="ts-time-group">
                            <span className="ts-time-tag">End Time</span>
                            <input
                              type="time"
                              name="hiTeaEndTime"
                              className="ts-time-field"
                              value={formData.hiTeaEndTime}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="ts-meal-card">
                        <div className="ts-meal-header">
                          <span className="ts-meal-emoji">🌙</span>
                          <h5>Dinner</h5>
                        </div>
                        <div className="ts-meal-times">
                          <div className="ts-time-group">
                            <span className="ts-time-tag">Start Time</span>
                            <input
                              type="time"
                              name="dinnerStartTime"
                              className="ts-time-field"
                              value={formData.dinnerStartTime}
                              onChange={handleChange}
                            />
                          </div>
                          <div className="ts-time-group">
                            <span className="ts-time-tag">End Time</span>
                            <input
                              type="time"
                              name="dinnerEndTime"
                              className="ts-time-field"
                              value={formData.dinnerEndTime}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Teams (Only shown for team events) */}
              {currentStep === 2 && formData.eventTypeCode === 'TEAM' && (
                <div className="ts-step-panel">
                  <div className="ts-add-section row bg-light py-5">
                    <h3 className="text-center mb-4">ADD TEAMS</h3>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="ts-field-group">
                          <label htmlFor="teamName">Team Name:</label>
                          <input
                            id="teamName"
                            type="text"
                            name="teamName"
                            className="ts-input-field"
                            value={team.teamName}
                            onChange={handleTeamChange}
                            placeholder="Enter team name"
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="ts-field-group">
                          <label htmlFor="teamLogo">Team Logo:</label>
                          <input
                            id="teamLogo"
                            type="file"
                            name="teamLogo"
                            className="ts-input-field"
                            onChange={handleTeamFileChange}
                            accept="image/*"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mb-3">
                      <button type="button" className="ts-btn ts-btn-secondary" onClick={addTeam}>
                        <span>+</span> Add Team
                      </button>
                    </div>

                    <div className="col-md-12 ts-display-section">
                      <h4 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                        Added Teams ({formData.teams.length})
                      </h4>
                      {formData.teams.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
                          {formData.teams.map((team, index) => (
                            <div 
                              key={index} 
                              style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: '20px',
                                backgroundColor: 'white',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              {/* Team Header */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '16px', borderBottom: '2px solid #f3f4f6' }}>
                                <div style={{ 
                                  width: '50px', 
                                  height: '50px', 
                                  borderRadius: '10px',
                                  overflow: 'hidden',
                                  backgroundColor: '#f3f4f6',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '20px',
                                  fontWeight: '700',
                                  color: '#6b7280'
                                }}>
                                  {team.teamLogo ? (
                                    <img
                                      src={
                                        typeof team.teamLogo === 'string'
                                          ? team.teamLogo
                                          : URL.createObjectURL(team.teamLogo)
                                      }
                                      alt={team.teamName}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                    team.teamName.charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                                    {team.teamName}
                                  </h5>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '4px',
                                    marginTop: '4px',
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    fontWeight: '500'
                                  }}>
                                    <span>👥</span>
                                    <span>{team.members?.length || 0} Members</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Members List */}
                              <div style={{ marginBottom: '16px' }}>
                                {team.members && team.members.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {team.members.map((member, mIndex) => (
                                      <div 
                                        key={mIndex} 
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          padding: '10px 12px',
                                          backgroundColor: '#f9fafb',
                                          borderRadius: '8px',
                                          border: '1px solid #f3f4f6'
                                        }}
                                      >
                                        <div style={{ flex: 1 }}>
                                          <div style={{ 
                                            fontWeight: '600', 
                                            fontSize: '14px',
                                            color: '#111827',
                                            marginBottom: '2px'
                                          }}>
                                            {member.firstName && member.lastName 
                                              ? `${member.firstName} ${member.lastName}` 
                                              : member.name || 'Unknown Member'}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            {member.email}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => removeMemberFromTeam(index, mIndex)}
                                          style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            backgroundColor: '#fee2e2',
                                            color: '#dc2626',
                                            cursor: 'pointer',
                                            fontSize: '18px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fecaca';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fee2e2';
                                          }}
                                          title="Remove member"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div style={{ 
                                    textAlign: 'center', 
                                    padding: '20px',
                                    color: '#9ca3af',
                                    fontSize: '13px'
                                  }}>
                                    No members yet
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons */}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  type="button"
                                  className="ts-btn ts-btn-sm ts-btn-secondary"
                                  onClick={() => openMemberModal(index)}
                                  style={{
                                    flex: 1,
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    borderRadius: '8px'
                                  }}
                                >
                                  <span style={{ fontSize: '16px' }}>+</span> Add Member
                                </button>
                                <button
                                  type="button"
                                  className="ts-btn ts-btn-danger ts-btn-sm"
                                  onClick={() => {
                                    if (window.confirm(`Remove team "${team.teamName}"?`)) {
                                      setFormData(prevData => ({
                                        ...prevData,
                                        teams: prevData.teams.filter((_, i) => i !== index),
                                      }));
                                    }
                                  }}
                                  style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    borderRadius: '8px'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '40px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '12px',
                          color: '#6b7280'
                        }}>
                          No teams added yet
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Sponsors - DROPDOWN SELECTION */}
              {currentStep === 3 && (
                <div className="ts-step-panel">
                  <div className="ts-add-section row bg-light py-5">
                    <h3 className="text-center mb-4">ASSIGN SPONSORS TO TOURNAMENT</h3>
                    
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <div className="ts-field-group">
                          <label htmlFor="selectSponsor">Select Sponsor: *</label>
                          <select
                            id="selectSponsor"
                            name="sponsorId"
                            className="ts-input-field"
                            value={selectedSponsor.sponsorId}
                            onChange={handleSponsorSelectionChange}
                            required
                          >
                            <option value="">Choose a sponsor...</option>
                            {allSponsors.map(sponsor => (
                              <option key={sponsor.id} value={sponsor.id}>
                                {sponsor.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-4 mb-3">
                        <div className="ts-field-group">
                          <label htmlFor="selectSponsorCategory">Sponsor Category: *</label>
                          <select
                            id="selectSponsorCategory"
                            name="sponsorCategoryId"
                            className="ts-input-field"
                            value={selectedSponsor.sponsorCategoryId}
                            onChange={handleSponsorSelectionChange}
                            required
                          >
                            <option value="">Select category...</option>
                            {sponsorCategories.map(category => (
                              <option key={category.id} value={category.id}>
                                {category.displayName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-4 mb-3">
                        <div className="ts-field-group">
                          <label htmlFor="agreementDetails">Agreement Details:</label>
                          <input
                            id="agreementDetails"
                            type="text"
                            name="agreementDetails"
                            className="ts-input-field"
                            value={selectedSponsor.agreementDetails}
                            onChange={handleSponsorSelectionChange}
                            placeholder="Optional agreement details"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mb-3">
                      <button 
                        type="button" 
                        className="ts-btn ts-btn-secondary" 
                        onClick={addSponsorToTournament}
                        disabled={!selectedSponsor.sponsorId || !selectedSponsor.sponsorCategoryId}
                      >
                        <span>+</span> Assign Sponsor
                      </button>
                    </div>

                    <div className="col-md-12 ts-display-section">
                      <h4>Assigned Sponsors ({formData.sponsors.length})</h4>
                      {formData.sponsors.length > 0 ? (
                        <table className="ts-data-table table table-striped">
                          <thead>
                            <tr>
                              <th>Logo</th>
                              <th>Sponsor Name</th>
                              <th>Category</th>
                              <th>Agreement Details</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.sponsors.map((sponsor, index) => {
                              const category = sponsorCategories.find(cat => cat.id === sponsor.sponsorCategoryId);
                              return (
                                <tr key={index}>
                                  <td>
                                    {sponsor.sponsorLogo ? (
                                      <img 
                                        src={sponsor.sponsorLogo} 
                                        alt={sponsor.sponsorName}
                                        style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          objectFit: 'contain',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    ) : (
                                      <div style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: '#6b7280'
                                      }}>
                                        N/A
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ fontWeight: '600' }}>{sponsor.sponsorName}</td>
                                  <td>{category?.displayName || 'N/A'}</td>
                                  <td>{sponsor.agreementDetails || '-'}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="ts-btn ts-btn-danger ts-btn-sm"
                                      onClick={() => {
                                        setFormData(prevData => ({
                                          ...prevData,
                                          sponsors: prevData.sponsors.filter((_, i) => i !== index),
                                        }));
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-center text-muted">No sponsors assigned yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="ts-nav-buttons">
                <button
                  type="button"
                  className="ts-btn ts-btn-outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1 || loading}
                >
                  ← Previous
                </button>

                <button
                  type="button"
                  className="ts-btn ts-btn-primary"
                  onClick={handleNext}
                  disabled={loading || (currentStep === 1 && !validateStep1())}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing...
                    </>
                  ) : currentStep === 3 ? (
                    '✓ Save & Finish'
                  ) : (
                    'Save & Next →'
                  )}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Add Member Modal */}
      {showMemberModal && (
        <div className="ts-modal-overlay" onClick={() => setShowMemberModal(false)}>
          <div 
            className="ts-modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px', width: '90%' }}
          >
            <div className="ts-modal-header">
              <h3>Add Team Member</h3>
              <button 
                className="ts-modal-close"
                onClick={() => {
                  setShowMemberModal(false);
                  setMemberSearchQuery('');
                  setShowMemberDropdown(false);
                }}
              >
                ×
              </button>
            </div>
            <div className="ts-modal-body" style={{ padding: '24px', minHeight: '200px' }}>
              <div className="ts-field-group mb-3">
                <label htmlFor="searchMember" style={{ marginBottom: '8px', display: 'block', fontWeight: '600' }}>
                  Search Member: *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="searchMember"
                    type="text"
                    className="ts-input-field"
                    value={memberSearchQuery}
                    onChange={handleMemberSearch}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2563eb';
                      setShowMemberDropdown(true);
                      filterAvailableMembers(memberSearchQuery, selectedTeamIndex);
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#ddd';
                      setTimeout(() => setShowMemberDropdown(false), 200);
                    }}
                    placeholder="Type name, email, or phone..."
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '2px solid #ddd',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                  
                  {showMemberDropdown && filteredMembers.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      maxHeight: '240px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      zIndex: 1001
                    }}>
                      {filteredMembers.map((member, index) => (
                        <div
                          key={member.id}
                          onMouseDown={() => handleMemberSelect(member.id)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            borderBottom: index < filteredMembers.length - 1 ? '1px solid #f3f4f6' : 'none',
                            transition: 'all 0.2s',
                            backgroundColor: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          <div style={{ 
                            fontWeight: '600', 
                            marginBottom: '4px',
                            color: '#111827',
                            fontSize: '14px'
                          }}>
                            {member.firstName} {member.lastName}
                          </div>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#6b7280',
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center'
                          }}>
                            <span>📧 {member.email}</span>
                            <span style={{ color: '#d1d5db' }}>|</span>
                            <span>📱 {member.mobileNumber}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small 
                  className="ts-helper-text" 
                  style={{ 
                    display: 'block', 
                    marginTop: '8px', 
                    fontSize: '13px',
                    color: '#6b7280'
                  }}
                >
                  {filteredMembers.length === 0 && memberSearchQuery
                    ? '⚠️ No members found matching your search' 
                    : filteredMembers.length === 0 && !memberSearchQuery
                    ? '⚠️ All members already added to this team'
                    : memberSearchQuery
                    ? `✓ ${filteredMembers.length} members found`
                    : `✓ ${filteredMembers.length} members available`
                  }
                </small>
              </div>
            </div>
            <div className="ts-modal-footer" style={{ padding: '16px 24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="ts-btn ts-btn-outline"
                onClick={() => {
                  setShowMemberModal(false);
                  setMemberSearchQuery('');
                  setShowMemberDropdown(false);
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ts-btn ts-btn-primary"
                onClick={() => {
                  addMemberToTeam();
                  setShowMemberModal(false);
                  setMemberSearchQuery('');
                  setShowMemberDropdown(false);
                }}
                disabled={!selectedMemberId}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: selectedMemberId ? 1 : 0.5,
                  cursor: selectedMemberId ? 'pointer' : 'not-allowed'
                }}
              >
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TournamentSetup;